import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Check, AlertCircle } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRIES = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let active = true;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setBooking(data);
          setState("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState("error");
          return;
        }
        if (tries >= MAX_TRIES) {
          setBooking(data);
          setState("pending");
          return;
        }
        tries += 1;
        setTimeout(poll, 2000);
      } catch {
        if (active) setState("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <main className="min-h-[100svh] flex items-center justify-center px-6 py-24 relative">
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="relative z-10 w-full max-w-xl ornate-frame bg-[var(--surface)] p-10 sm:p-14 text-center">
        <img src={ASSETS.crest} alt="" className="w-16 mx-auto mb-6 rounded-full" />

        {state === "checking" && (
          <div data-testid="success-checking">
            <Loader2 className="w-8 h-8 text-[var(--gold)] animate-spin mx-auto" />
            <p className="font-serif-alt text-xl text-[var(--cream)] mt-6">Confirming your payment…</p>
          </div>
        )}

        {state === "paid" && booking && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="success-confirmed">
            <div className="w-14 h-14 rounded-full bg-[var(--gold)] flex items-center justify-center mx-auto mb-6">
              <Check className="w-7 h-7 text-[var(--bg)]" />
            </div>
            <p className="uppercase tracking-[0.35em] text-[11px] text-[var(--gold)]">You're booked in</p>
            <h1 className="font-display text-4xl sm:text-5xl text-[var(--cream)] mt-3">See you there</h1>
            <p className="font-serif-alt text-lg text-[var(--muted)] mt-4">
              A confirmation has been sent to <span className="text-[var(--cream)]">{booking.email}</span>.
            </p>
            <div className="mt-8 border-t border-b border-[rgba(201,162,74,0.2)] py-6">
              <p className="uppercase tracking-[0.2em] text-[11px] text-[var(--muted)]">Booking reference</p>
              <p data-testid="booking-ref" className="font-display text-3xl text-[var(--gold)] tracking-[0.15em] mt-2">{booking.booking_ref}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6 text-left">
              <div>
                <p className="uppercase tracking-[0.2em] text-[10px] text-[var(--muted)]">Tickets</p>
                <p className="font-serif-alt text-xl text-[var(--cream)]">{booking.quantity}</p>
              </div>
              <div>
                <p className="uppercase tracking-[0.2em] text-[10px] text-[var(--muted)]">Total paid</p>
                <p className="font-serif-alt text-xl text-[var(--cream)]">£{(booking.amount / 100).toFixed(2)}</p>
              </div>
            </div>
            <Link to="/" data-testid="success-home-link" className="inline-block mt-10 uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] border-b border-[var(--gold)] pb-1">
              Back to the event
            </Link>
          </motion.div>
        )}

        {state === "pending" && (
          <div data-testid="success-pending">
            <Loader2 className="w-8 h-8 text-[var(--gold)] animate-spin mx-auto" />
            <p className="font-serif-alt text-xl text-[var(--cream)] mt-6">Your payment is processing.</p>
            <p className="font-body text-sm text-[var(--muted)] mt-3">We'll email your ticket to you shortly. You can safely close this page.</p>
            <Link to="/" className="inline-block mt-8 uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] border-b border-[var(--gold)] pb-1">Back to the event</Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="success-error">
            <AlertCircle className="w-10 h-10 text-[var(--gold)] mx-auto" />
            <p className="font-serif-alt text-xl text-[var(--cream)] mt-6">We couldn't confirm this payment.</p>
            <p className="font-body text-sm text-[var(--muted)] mt-3">If you were charged, please contact hello@horseshoepub.co.uk with any reference.</p>
            <Link to="/" className="inline-block mt-8 uppercase tracking-[0.2em] text-[11px] text-[var(--gold)] border-b border-[var(--gold)] pb-1">Back to the event</Link>
          </div>
        )}
      </div>
    </main>
  );
}
